import React, { useState, useEffect } from "react";
import { useFormatter } from "../../../hooks/useFomatter";
import { useServerApi } from "../../../hooks/userServerApi";
import { IconSearch } from "@tabler/icons";
import {
  Button,
  Text,
  Title,
  Badge,
  Accordion,
  LoadingOverlay,
  Box,
  Paper,
  Table,
  Group,
  Stack,
} from "@mantine/core";
import { DatePicker } from "@mantine/dates";
import moment from "moment";
import EntityTabAnchor from "./EntityTabAnchor";
import DuplicateBidConflictsAlert from "./DuplicateBidConflictsAlert";

const CommissionStatementsTab = ({ form }) => {
  const [api] = useServerApi();
  const [formatter] = useFormatter();

  const [loading, setLoading] = useState(false);
  const [fromPeriod, setFromPeriod] = useState(null);
  const [toPeriod, setToPeriod] = useState(null);
  const [statements, setStatements] = useState([]);
  const [conflicts, setConflicts] = useState([]);

  const payrollStatus = form?.values?.status;
  const isConfirmed =
    payrollStatus === "CONFIRMED" || payrollStatus === "COMPLETED";

  const loadSkippedDuplicateConflicts = async () => {
    if (!form?.values?._id) {
      setConflicts([]);
      return;
    }
    try {
      const result = await api.Payroll2.getDuplicateBidConflicts(
        form.values._id,
      );
      if (result?.success) {
        setConflicts(
          result.data?.excludedConflicts ||
            result.data?.skippedDuplicateSources ||
            result.data?.conflicts ||
            [],
        );
      }
    } catch (error) {
      console.log("loadSkippedDuplicateConflicts error:", error);
    }
  };

  useEffect(() => {
    loadSkippedDuplicateConflicts();
  }, [form?.values?._id]);

  useEffect(() => {
    const loadSavedStatements = async () => {
      const savedSearch = form?.values?.commissionStatementSearch;
      if (savedSearch) {
        if (savedSearch.fromPeriod)
          setFromPeriod(new Date(savedSearch.fromPeriod));
        if (savedSearch.toPeriod) setToPeriod(new Date(savedSearch.toPeriod));

        try {
          setLoading(true);
          const result = await api.Payroll2.getCommissionStatementsForPayroll(
            moment(savedSearch.fromPeriod).format("YYYY-MM-DD"),
            moment(savedSearch.toPeriod).format("YYYY-MM-DD"),
            null,
          );
          if (result?.success && result.data?.statements) {
            setStatements(result.data.statements);
          }
        } catch (error) {
          console.log("Error loading saved statements:", error);
        } finally {
          setLoading(false);
        }
      }
    };

    loadSavedStatements();
  }, [form?.values?._id]);

  const saveStatementsToPayroll = async (statementIds) => {
    if (!form?.values?._id || !form?.updateAndSave) return;
    try {
      await form.updateAndSave({
        commissionStatements: statementIds,
        commissionStatementSearch: {
          fromPeriod,
          toPeriod,
        },
      });
    } catch (error) {
      console.log("Error saving commission statements to payroll:", error);
    }
  };

  const handleSearch = async () => {
    if (!fromPeriod || !toPeriod) {
      return;
    }

    try {
      setLoading(true);
      const result = await api.Payroll2.getCommissionStatementsForPayroll(
        moment(fromPeriod).format("YYYY-MM-DD"),
        moment(toPeriod).format("YYYY-MM-DD"),
        form?.values?._id,
      );

      if (result && result.success && result.data && result.data.statements) {
        setStatements(result.data.statements);
        setConflicts(result.data.excludedConflicts || result.data.conflicts || []);
        const statementIds = result.data.statements.map((s) => s._id);
        await saveStatementsToPayroll(statementIds);
        console.log(
          `Loaded ${result.data.count} commission statements (no pagination limit)`,
        );
      } else {
        setStatements([]);
        setConflicts([]);
      }
    } catch (error) {
      console.log("Error fetching commission statements:", error);
      setStatements([]);
    } finally {
      setLoading(false);
    }
  };

  const getConfirmedRowCount = (rows) => {
    if (!rows) return 0;
    return rows.filter((r) => r.status === "CONFIRMED").length;
  };

  const getConfirmedCommission = (rows) => {
    if (!rows) return 0;
    return rows
      .filter((r) => r.status === "CONFIRMED")
      .reduce((sum, r) => sum + (r.totalRcv || 0), 0);
  };

  return (
    <Box pos="relative">
      <LoadingOverlay visible={loading} overlayBlur={2} />

      <Paper p="md" withBorder mb="lg">
        <Title order={5} mb="md">
          Search Confirmed Commission Statements
        </Title>

        <Group align="flex-end" spacing="md">
          <DatePicker
            label="Period From"
            size="xs"
            placeholder="Select start month"
            value={fromPeriod}
            onChange={setFromPeriod}
            allowFreeInput
            clearable
            disabled={isConfirmed}
          />

          <DatePicker
            label="Period To"
            size="xs"
            placeholder="Select end month"
            value={toPeriod}
            onChange={setToPeriod}
            allowFreeInput
            clearable
            disabled={isConfirmed}
          />

          <Button
            size="xs"
            leftIcon={<IconSearch size={14} />}
            onClick={handleSearch}
            disabled={!fromPeriod || !toPeriod || isConfirmed}
          >
            Search
          </Button>
        </Group>

        <Text size="xs" color="dimmed" mt="xs">
          Note: Only showing statements with CONFIRMED status. Period format:
          YYYYMM
        </Text>
        {isConfirmed && (
          <Text size="xs" color="orange" mt="xs">
            Payroll is completed. No more edits allowed.
          </Text>
        )}
      </Paper>

      <Box mb="md">
        <DuplicateBidConflictsAlert conflicts={conflicts} />
      </Box>

      {statements.length > 0 && (
        <Paper p="md" withBorder>
          <Group position="apart" mb="md">
            <Title order={5}>Results</Title>
            <Badge size="lg">{statements.length} statements found</Badge>
          </Group>

          <Accordion variant="separated">
            {statements.map((statement) => (
              <Accordion.Item key={statement._id} value={statement._id}>
                <Accordion.Control>
                  <Group position="apart" noWrap>
                    <Group spacing="xl" noWrap>
                      <Box w={120}>
                        <EntityTabAnchor
                          entity="commissionStatement"
                          id={statement._id}
                          label={statement.code}
                          size="sm"
                          weight={600}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <Text size="xs" color="dimmed">
                          {statement.period}
                        </Text>
                      </Box>

                      <Box w={200}>
                        <Text size="sm" weight={500} lineClamp={1}>
                          {statement.provider?.name ||
                            statement.provider?.code ||
                            "-"}
                        </Text>
                        <Text size="xs" color="dimmed">
                          Provider
                        </Text>
                      </Box>

                      <Box w={80}>
                        <Group spacing={4}>
                          <Badge color="green" size="sm">
                            {getConfirmedRowCount(statement.rows)}
                          </Badge>
                          <Text size="xs" color="dimmed">
                            / {statement.rows?.length || 0}
                          </Text>
                        </Group>
                        <Text size="xs" color="dimmed">
                          Rows
                        </Text>
                      </Box>

                      <Box w={120}>
                        <Text size="sm" weight={500} color="green">
                          {formatter.currency(
                            getConfirmedCommission(statement.rows),
                          )}
                        </Text>
                        <Text size="xs" color="dimmed">
                          Confirmed
                        </Text>
                      </Box>
                    </Group>

                    <Badge color="green" size="sm">
                      {statement.status}
                    </Badge>
                  </Group>
                </Accordion.Control>

                <Accordion.Panel>
                  <Stack spacing="md" mt="md">
                    <Group spacing="xl">
                      <Box>
                        <Text size="xs" color="dimmed">
                          Total Commission
                        </Text>
                        <Text size="sm" weight={500}>
                          {formatter.currency(statement.totalCommission || 0)}
                        </Text>
                      </Box>
                      <Box>
                        <Text size="xs" color="dimmed">
                          Confirmed Commission
                        </Text>
                        <Text size="sm" weight={500} color="green">
                          {formatter.currency(
                            getConfirmedCommission(statement.rows),
                          )}
                        </Text>
                      </Box>
                      <Box>
                        <Text size="xs" color="dimmed">
                          Total Rows
                        </Text>
                        <Text size="sm">{statement.rows?.length || 0}</Text>
                      </Box>
                      <Box>
                        <Text size="xs" color="dimmed">
                          Confirmed Rows
                        </Text>
                        <Badge color="green" size="sm">
                          {getConfirmedRowCount(statement.rows)}
                        </Badge>
                      </Box>
                    </Group>

                    {statement.rows && statement.rows.length > 0 && (
                      <>
                        <Text size="sm" weight={500}>
                          Statement Rows:
                        </Text>
                        <Table fontSize="xs" striped highlightOnHover>
                          <thead>
                            <tr>
                              <th>#</th>
                              <th>Order</th>
                              <th>DN</th>
                              <th>Policy</th>
                              <th>Client / Insured</th>
                              <th>Category</th>
                              <th>Premium</th>
                              <th>Total Rcv</th>
                              <th>Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {statement.rows.map((row, idx) => (
                              <React.Fragment key={idx}>
                                <tr>
                                  <td>{row.rowNumber || idx + 1}</td>
                                  <td>
                                    <EntityTabAnchor
                                      entity="order"
                                      id={row.order?._id}
                                      label={row.order?.code}
                                    />
                                  </td>
                                  <td>
                                    <EntityTabAnchor
                                      entity="debitNote"
                                      id={row.order?.debitNote?._id}
                                      label={row.order?.debitNote?.code}
                                    />
                                  </td>
                                  <td>
                                    <Text size="xs">{row.policy || "-"}</Text>
                                  </td>
                                  <td>
                                    <Text size="xs" lineClamp={1}>
                                      {row.insuredName ||
                                        row.order?.client?.name ||
                                        row.order?.client?.code ||
                                        "-"}
                                    </Text>
                                  </td>
                                  <td>
                                    <Text size="xs" lineClamp={1}>
                                      {row.order?.debitNote?.productType
                                        ?.name ||
                                        row.order?.product?.type?.name ||
                                        row.planCode ||
                                        row.order?.product?.name ||
                                        "-"}
                                    </Text>
                                  </td>
                                  <td>
                                    <Text size="xs">
                                      {formatter.currency(
                                        row.premiumReceived || 0,
                                      )}
                                    </Text>
                                  </td>
                                  <td>
                                    <Text size="xs" weight={500}>
                                      {formatter.currency(row.totalRcv || 0)}
                                    </Text>
                                  </td>
                                  <td>
                                    <Badge
                                      color={
                                        row.status === "CONFIRMED"
                                          ? "green"
                                          : row.status === "VERIFIED"
                                            ? "blue"
                                            : "gray"
                                      }
                                      size="xs"
                                    >
                                      {row.status || "PENDING"}
                                    </Badge>
                                  </td>
                                </tr>
                                {row.internalRemark?.trim() && (
                                  <tr>
                                    <td colSpan={9} style={{ paddingTop: 0 }}>
                                      <Text
                                        size="xs"
                                        color="dimmed"
                                        italic
                                        lineClamp={2}
                                        title={row.internalRemark.trim()}
                                      >
                                        {row.internalRemark.trim()}
                                      </Text>
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            ))}
                          </tbody>
                        </Table>
                      </>
                    )}
                  </Stack>
                </Accordion.Panel>
              </Accordion.Item>
            ))}
          </Accordion>
        </Paper>
      )}

      {statements.length === 0 && !loading && (
        <Paper p="xl" withBorder>
          <Text color="dimmed" align="center">
            No confirmed statements found. Please enter period range and click
            Search.
          </Text>
        </Paper>
      )}
    </Box>
  );
};

export default CommissionStatementsTab;
